import { supabase, supabaseAdmin } from '../lib/supabase';
import { logAction, getCurrentAdmin } from './logger';

export interface ValidationRow {
  id: string;
  user_id: string | null;
  bar_id: string;
  match_id: string;
  validation_type: 'confirming' | 'denying';
  created_at: string;
  bars: { id: string; name: string } | null;
  matches: { id: string; home_team: string; away_team: string; match_date: string; match_time: string; competition: string } | null;
}

export interface ValidationGroup {
  bar_id: string;
  bar_name: string;
  match_id: string;
  match_label: string;
  match_date: string;
  match_time: string;
  competition: string;
  confirmations: number;
  denials: number;
  votes: ValidationRow[];
}

/** Charge tous les votes pour les matchs à partir d'aujourd'hui (limite 1000). */
export async function listRecentValidations(): Promise<ValidationRow[]> {
  const today = new Date().toISOString().split('T')[0];
  const { data, error } = await supabase
    .from('bar_validations')
    .select(`
      id, user_id, bar_id, match_id, validation_type, created_at,
      bars:bar_id (id, name),
      matches:match_id (id, home_team, away_team, match_date, match_time, competition)
    `)
    .order('created_at', { ascending: false })
    .limit(1000);
  if (error) throw error;
  return (data || []).filter((r: any) => r.matches?.match_date >= today) as unknown as ValidationRow[];
}

/** Groupe les votes par couple bar × match, trié par activité (plus récents en haut). */
export function groupValidations(rows: ValidationRow[]): ValidationGroup[] {
  const groups = new Map<string, ValidationGroup>();
  for (const r of rows) {
    const key = `${r.bar_id}__${r.match_id}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        bar_id: r.bar_id,
        match_id: r.match_id,
        bar_name: r.bars?.name || '?',
        match_label: r.matches ? `${r.matches.home_team} vs ${r.matches.away_team}` : '?',
        match_date: r.matches?.match_date || '',
        match_time: r.matches?.match_time || '',
        competition: r.matches?.competition || '',
        confirmations: 0,
        denials: 0,
        votes: [],
      };
      groups.set(key, g);
    }
    g.votes.push(r);
    if (r.validation_type === 'confirming') g.confirmations++;
    else g.denials++;
  }
  return Array.from(groups.values()).sort((a, b) => {
    // Tri : date du match croissante, puis nb total de votes décroissant
    if (a.match_date !== b.match_date) return a.match_date.localeCompare(b.match_date);
    return (b.confirmations + b.denials) - (a.confirmations + a.denials);
  });
}

export async function revokeValidation(row: ValidationRow): Promise<void> {
  const admin = getCurrentAdmin();
  const { error } = await supabaseAdmin
    .from('bar_validations')
    .delete()
    .eq('id', row.id);
  if (error) throw error;
  const matchLabel = row.matches ? `${row.matches.home_team} vs ${row.matches.away_team}` : row.match_id;
  const barName = row.bars?.name || row.bar_id;
  await logAction(
    'revoke_validation',
    'bar_validation',
    row.id,
    { type: row.validation_type, user_id: row.user_id, bar: barName, match: matchLabel },
    null,
    `${admin?.name || 'admin'} a révoqué un vote "${row.validation_type === 'confirming' ? 'Ça diffuse' : 'Ça diffuse pas'}" sur ${barName} pour ${matchLabel}`
  );
}

export async function forceDiffusionStatus(
  barId: string,
  matchId: string,
  newStatus: 'confirmed' | 'probable' | 'unconfirmed',
  context: { barName: string; matchLabel: string }
): Promise<void> {
  const admin = getCurrentAdmin();
  const adminEmail = admin?.email || 'admin';
  // Upsert : si la diffusion n'existe pas, on la crée. Sinon on update.
  const { data: existing } = await supabaseAdmin
    .from('diffusions')
    .select('id, status')
    .eq('bar_id', barId)
    .eq('match_id', matchId)
    .maybeSingle();

  const oldStatus = existing?.status || null;
  const payload = {
    status: newStatus,
    confirmed_at: new Date().toISOString(),
    confirmed_by: `admin:${adminEmail}`,
  };

  if (existing) {
    const { error } = await supabaseAdmin.from('diffusions').update(payload).eq('id', existing.id);
    if (error) throw error;
  } else {
    const { error } = await supabaseAdmin.from('diffusions').insert({
      bar_id: barId,
      match_id: matchId,
      ...payload,
    });
    if (error) throw error;
  }

  const labels: Record<string, string> = { unconfirmed: 'non vérifié', probable: 'probable', confirmed: 'confirmé' };
  await logAction(
    'force_diffusion_status',
    'diffusion',
    existing?.id || null,
    { status: oldStatus },
    { status: newStatus },
    `${admin?.name || 'admin'} a forcé ${context.barName} en "${labels[newStatus]}" pour ${context.matchLabel}`
  );
}
